import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { feedbackApi, FEEDBACK_CATEGORY_LABELS, type FeedbackItem } from '@/lib/api/feedback';
import { queryKeys } from '@/lib/queryKeys';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatTimestamp } from '@/lib/date';

export const Route = createFileRoute('/admin/student-feedback')({
  component: StudentFeedbackPage,
});

function StudentFeedbackPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.feedback.list(),
    queryFn: feedbackApi.list,
  });

  const items = data?.items ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Student Feedback</h1>
        <p className="text-sm text-muted-foreground">
          Messages submitted from the student Help page. Visible to admins only.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">Failed to load feedback.</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No feedback submitted yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((f: FeedbackItem) => (
            <Card key={f.id}>
              <CardContent className="p-4">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="inline-flex items-center rounded-full bg-[#e8660d]/10 px-2.5 py-0.5 text-xs font-semibold text-[#e8660d]">
                    {FEEDBACK_CATEGORY_LABELS[f.category] ?? f.category}
                  </span>
                  <span className="text-xs text-muted-foreground">{formatTimestamp(f.created_at)}</span>
                </div>
                <p className="whitespace-pre-wrap text-sm text-foreground">{f.message}</p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {f.student_name || 'Unknown'}{f.student_email ? ` · ${f.student_email}` : ''}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
